
  import { defineComponent, markRaw, PropType, ref } from 'vue';
  import L from 'leaflet';
  import 'leaflet/dist/leaflet.css';

  import MapPopup from '@/common/components/redesigned/popups/MapPopup.vue';

  import Loader from '@/components/Loader.vue';

  import MapLegend from './MapLegend.vue';
  import type {
    Feature,
    FeatureCodeFn,
    FeatureSelectedFn,
    FeatureStyleFn,
    FeatureTooltipFn,
    GeoJsonObject,
    MapConfig,
    MapLegendConfig,
  } from './types';

  const OUTLINE_PANE = 'selectedOutlinePane';

  type MapComponentData = {
    map: Nullable<L.Map>;
    geoJsonLayer: Nullable<L.GeoJSON>;
    outlineLayer: Nullable<L.GeoJSON>;
    resizeObserver: Nullable<ResizeObserver>;
  };

  export default defineComponent({
    name: 'MapComponent',
    components: { Loader, MapLegend, MapPopup },
    props: {
      geojson: {
        type: Object as PropType<GeoJsonObject>,
        required: true,
      },
      mapConfig: {
        type: Object as PropType<MapConfig>,
        required: true,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      stateMessage: {
        type: String,
        default: '',
      },
      height: {
        type: String,
        default: '480px',
      },
      styleFeature: {
        type: Function as PropType<FeatureStyleFn>,
        default: undefined,
      },
      getFeatureCode: {
        type: Function as PropType<FeatureCodeFn>,
        default: (feature?: Feature) => Number(feature?.properties?.iso_n3),
      },
      featureTooltip: {
        type: Function as PropType<FeatureTooltipFn>,
        default: undefined,
      },
      selectedCode: {
        type: Number as PropType<Nullable<number>>,
        default: null,
      },
      isFeatureSelected: {
        type: Function as PropType<FeatureSelectedFn>,
        default: undefined,
      },
      outlineColor: {
        type: String,
        default: '#ffffff',
      },
      legend: {
        type: Object as PropType<MapLegendConfig | null>,
        default: null,
      },
      popupOpen: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['featureClick', 'ready', 'closePopup'],
    setup() {
      const container = ref<HTMLElement | null>(null);
      return { container };
    },
    data(): MapComponentData {
      return {
        map: null,
        geoJsonLayer: null,
        outlineLayer: null,
        resizeObserver: null,
      };
    },
    computed: {
      getSizeStyle(): Record<string, string> {
        return { height: this.height };
      },
    },
    mounted() {
      this.rebuild();
    },
    beforeUnmount() {
      this.destroyMap();
    },
    methods: {
      rebuild() {
        this.destroyMap();
        this.$nextTick(() => {
          if (this.isLoading || this.stateMessage) return;
          this.initMap();
        });
      },

      initMap() {
        if (!this.container || this.map) return;

        this.map = markRaw(
          L.map(this.container, {
            maxZoom: this.mapConfig.maxZoom,
            minZoom: this.mapConfig.minZoom,
            zoomControl: true,
          }).setView(this.mapConfig.center, this.mapConfig.zoom),
        );

        this.map.createPane(OUTLINE_PANE);

        this.geoJsonLayer = markRaw(
          L.geoJSON(this.geojson, {
            style: (feature?: Feature) => {
              const customStyle = this.styleFeature ? this.styleFeature(feature as Feature) : {};

              return {
                weight: 0.5,
                fillOpacity: 1,
                ...customStyle,
              };
            },
            onEachFeature: this.onEachFeature,
          }).addTo(this.map),
        );

        this.geoJsonLayer.on('tooltipopen', this.onTooltipOpen);
        this.map.on('dragstart', this.closeAllTooltips);

        this.drawOutline();
        this.observeContainerResize();
        this.$emit('ready', this.map);
      },

      onTooltipOpen(event: L.LeafletEvent): void {
        const opened = (event as L.LeafletEvent & { sourceTarget?: L.Layer }).sourceTarget;
        this.geoJsonLayer?.eachLayer((layer) => {
          if (layer !== opened) (layer as L.Layer).closeTooltip?.();
        });
      },

      closeAllTooltips(): void {
        this.geoJsonLayer?.eachLayer((layer) => (layer as L.Layer).closeTooltip?.());
      },

      observeContainerResize() {
        if (typeof ResizeObserver === 'undefined' || !this.container) return;

        this.resizeObserver = markRaw(new ResizeObserver(() => this.map?.invalidateSize()));
        this.resizeObserver.observe(this.container);
      },

      destroyMap() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.outlineLayer?.remove();
        this.outlineLayer = null;
        this.geoJsonLayer?.remove();
        this.geoJsonLayer = null;

        if (!this.map) return;

        this.map.off();
        this.map.remove();
        this.map = null;
      },

      resolveCode(feature?: Feature): Nullable<number> {
        return this.getFeatureCode(feature);
      },

      onEachFeature(feature: Feature, layer: L.Layer): void {
        const tooltip = this.featureTooltip?.(feature);
        if (tooltip) layer.bindTooltip(tooltip, { sticky: true });

        layer.on('mouseup', () => this.$emit('featureClick', feature));
      },

      // eslint-disable-next-line vue/no-unused-properties
      restyle() {
        if (!this.geoJsonLayer) return;

        this.geoJsonLayer.eachLayer((layer) => {
          const featureLayer = layer as L.Path & { feature?: Feature };
          if (this.styleFeature) featureLayer.setStyle(this.styleFeature(featureLayer.feature));
        });

        this.drawOutline();
      },

      isFeatureSelectedInternal(feature?: Feature): boolean {
        return this.isFeatureSelected
          ? this.isFeatureSelected(feature)
          : this.selectedCode != null && this.resolveCode(feature) === this.selectedCode;
      },

      drawOutline() {
        this.outlineLayer?.remove();
        this.outlineLayer = null;

        if (!this.map || !this.geoJsonLayer) return;

        const selectedFeatures: Feature[] = [];
        this.geoJsonLayer.eachLayer((layer) => {
          const featureLayer = layer as L.Path & { feature?: Feature };
          if (featureLayer.feature && this.isFeatureSelectedInternal(featureLayer.feature)) {
            selectedFeatures.push(featureLayer.feature);
          }
        });

        if (!selectedFeatures.length) return;

        this.outlineLayer = markRaw(
          L.geoJSON(selectedFeatures as unknown as GeoJsonObject, {
            pane: OUTLINE_PANE,
            interactive: false,
            style: () => ({
              color: this.outlineColor,
              weight: 1,
              fillOpacity: 0,
              className: 'map-component-feature-selected',
            }),
          }).addTo(this.map),
        );
      },
    },
    watch: {
      geojson() {
        this.rebuild();
      },
      mapConfig() {
        this.rebuild();
      },
      isLoading() {
        this.rebuild();
      },
      stateMessage() {
        this.rebuild();
      },
      selectedCode() {
        this.drawOutline();
      },
    },
  });
